import { useState, useMemo, ReactNode } from 'react';
import {
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  SelectProps,
  TextField,
  Box,
  styled,
  SelectChangeEvent,
} from '@mui/material';

const StyledFormControl = styled(FormControl)(({ theme }) => ({
  '& .MuiInputLabel-root': {
    color: theme.palette.text.secondary,
    fontWeight: 500,
  },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: theme.palette.grey[300],
    },
    '&:hover fieldset': {
      borderColor: theme.palette.primary.main,
    },
    '&.Mui-focused fieldset': {
      borderColor: theme.palette.primary.main,
      borderWidth: '1px',
    },
  },
}));

interface Option {
  value: string | number;
  label: string;
}

interface StyledSelectProps<T = string | number> {
  label?: string;
  options: Option[];
  value?: T;
  onChange?: (event: SelectChangeEvent<T>, child?: ReactNode) => void;
  sx?: SelectProps['sx'];
  formControlSx?: SelectProps['sx'];
  startAdornment?: SelectProps['startAdornment'];
  disabled?: boolean;
  size?: SelectProps['size'];
  variant?: SelectProps['variant'];
  fullWidth?: boolean;
  footer?: ReactNode;
}

const StyledSelect = <T extends string | number = string>({
  label,
  options,
  value,
  onChange,
  sx,
  formControlSx,
  startAdornment,
  disabled,
  size,
  variant,
  fullWidth = true,
  footer,
}: StyledSelectProps<T>) => {
  const [searchTerm, setSearchTerm] = useState('');
  const labelId = label
    ? `${label.toLowerCase().replace(/\s/g, '-')}-select-label`
    : undefined;

  const safeValue = useMemo(() => {
    // If options haven't loaded yet, render as empty to avoid MUI warnings.
    // We'll re-render with the real value once options are available.
    if (options.length === 0) {
      return '' as unknown as T;
    }
    const hasMatch = options.some((o) => String(o.value) === String(value));
    if (!hasMatch) {
      return '' as unknown as T;
    }
    return value;
  }, [options, value]);

  const filteredOptions = useMemo(
    () =>
      options.filter((option) =>
        option.label.toLowerCase().includes(searchTerm.toLowerCase()),
      ),
    [options, searchTerm],
  );

  return (
    <StyledFormControl
      fullWidth={fullWidth}
      sx={formControlSx}
    >
      {label && <InputLabel id={labelId}>{label}</InputLabel>}
      <Select
        labelId={labelId}
        label={label}
        value={safeValue}
        onChange={onChange}
        sx={sx}
        startAdornment={startAdornment}
        disabled={disabled}
        size={size}
        variant={variant}
        MenuProps={{
          // Use a portal so the menu is not clipped by ancestor overflow
          disablePortal: false,
          PaperProps: {
            sx: {
              maxHeight: 300,
              zIndex: (theme) => theme.zIndex.modal + 1,
            },
          },
        }}
      >
        <Box
          onKeyDown={(e) => {
            e.stopPropagation();
          }}
          sx={{
            position: 'sticky',
            top: 0,
            zIndex: 1,
            backgroundColor: (theme) => theme.palette.background.paper,
            p: 1,
          }}
        >
          <TextField
            fullWidth
            variant='outlined'
            placeholder='Search...'
            size='small'
            value={searchTerm}
            onChange={(e) => {
              setSearchTerm(e.target.value);
            }}
          />
        </Box>
        {filteredOptions.map((option) => (
          <MenuItem
            key={option.value}
            value={option.value}
          >
            {option.label}
          </MenuItem>
        ))}
        {footer && (
          <Box
            role='presentation'
            sx={{
              borderTop: (theme) => `1px solid ${theme.palette.divider}`,
              mt: 0.5,
              pt: 0.5,
            }}
          >
            {footer}
          </Box>
        )}
      </Select>
    </StyledFormControl>
  );
};

export default StyledSelect;
